import {Filter} from "lucide-react";
import {useEffect, useRef, useState} from "react";
import {useForm} from "react-hook-form";
import {useTranslation} from "react-i18next";

import {Button, buttonVariants} from "@/components/ui/button";
import {cn} from "@/lib/utils";
import {Dialog, DialogContent, DialogTrigger} from "@/components/ui/dialog";
import {Form} from "@/components/ui/form";
import {Separator} from "@/components/ui/separator";

interface FiltersPopupProps {
  children: React.ReactNode;
  /** Rendered after the header separator, before the filter form (e.g. mobile-only sort). */
  beforeForm?: React.ReactNode;
  showBadge?: boolean;
  defaultValues: Record<string, any>;
  // Accept typed form submit handlers (contravariance vs Record breaks TS2322).
  onApply: (filters: any) => void;
  onClear?: () => void;
  variant?: "outline" | "default" | "ghost" | "link" | "secondary" | "destructive";
  size?: "xs" | "sm" | "lg" | "icon" | "icon-xs" | "icon-sm" | "icon-lg";
}

export default function FiltersPopup({
  showBadge = false,
  defaultValues,
  onApply,
  onClear,
  beforeForm,
  children,
  variant = "outline",
  size = "sm",
}: FiltersPopupProps) {
  const {t} = useTranslation("common");
  const [isOpen, setIsOpen] = useState(false);
  const submitedDataRef = useRef(null);
  const form = useForm({
    defaultValues,
  });
  const hasValues = Object.keys(defaultValues ?? {}).length > 0;

  const handleChange = (state: boolean) => {
    setIsOpen(state);
    if (!state) {
      form.reset(defaultValues, {
        keepValues: false,
      });
    }
  };

  const handleSubmit = (data: any) => {
    onApply(data);
    submitedDataRef.current = data;
    setIsOpen(false);
  };

  const handleClear = () => {
    const clearedFields = Object.keys(defaultValues).reduce(
      (acc, k) => {
        acc[k] = Array.isArray(defaultValues[k]) ? [] : null;
        return acc;
      },
      {} as Record<string, any>,
    );
    form.reset(clearedFields, {keepValues: false});
    if (onClear) {
      onClear();
    } else {
      onApply({});
    }
    setIsOpen(false);
  };

  useEffect(() => {
    if (form.formState.isSubmitSuccessful) {
      form.reset(submitedDataRef.current ?? {});
      submitedDataRef.current = null;
    }
  }, [form.formState.isSubmitSuccessful]);

  return (
    <Dialog
      open={isOpen}
      onOpenChange={handleChange}
      /** Portaled combobox/popover lists are outside the dialog node; default `modal` blocks pointer events on them. */
      modal="trap-focus"
    >
      <div className="relative">
        {showBadge && (
          <div className="bg-primary absolute -top-0.5 -right-0.5 size-2 rounded-full" />
        )}
        <DialogTrigger render={<Button variant={variant} size={size} />}>
          <Filter className="size-4" />
        </DialogTrigger>
      </div>
      <DialogContent className="w-full lg:w-[50vw]">
        <div className="flex flex-col gap-4">
          <div className="">
            <h4 className="leading-none font-medium">{t("filters")}</h4>
            <p className="text-muted-foreground text-sm">
              {t("filters_subtitle")}
            </p>
          </div>
          <Separator />
          {beforeForm && isOpen ? (
            <>
              {beforeForm}
              <Separator />
            </>
          ) : null}
          <Form {...form}>
            <form onSubmit={form.handleSubmit(handleSubmit)}>
              <div className="flex flex-col gap-4">{isOpen ? children : null}</div>
              <div className="mt-6 flex flex-col items-center justify-center gap-2">
                {/*
                  Base UI <Button> merges getButtonProps() after user props and forces type="button",
                  so type="submit" never wins — the form never submits. Use a native submit control.
                */}
                <button type="submit" className={cn(buttonVariants(), "w-[80%]")}>
                  {t("apply")}
                </button>
                <Button variant="link" type="button" onClick={handleClear} disabled={!hasValues}>
                  {t("clear")}
                </Button>
              </div>
            </form>
          </Form>
        </div>
      </DialogContent>
    </Dialog>
  );
}
